import { CircularProgress, Grid, Pagination, Paper, Tooltip } from '@mui/material';

import clsx from 'clsx';
import { subDays } from 'date-fns';
import { FieldArray, useFormikContext } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useBillableJobOfferingsQuery } from 'api/hooks/jobmarket/offers';
import ErrorBoundary from 'app/components/ErrorBoundary';
import InfoBox from 'app/components/InfoBox';
import { RichList } from 'app/components/RichList';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import { InvoiceFormValues } from '../validation';

interface Props {
  companyId: number;
}

export default function Offers(props: Props) {
  const { values, setFieldValue, validateForm } = useFormikContext<InvoiceFormValues>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [hasDisabledJobOffer, setHasDisabledJobOffer] = useState(false);
  const disabledDate = subDays(new Date(), 3);
  const [offset, setOffset] = useState(0);

  const {
    data: offersData,
    isError,
    isLoading
  } = useBillableJobOfferingsQuery({
    params: { paid: true, companyId: props.companyId, invoiceCreated: false, offset: offset }
  });

  const handleOfferSelect = async offer => {
    await Promise.all([
      setFieldValue('selectedOffers', [offer], false),
      setFieldValue('selectedPeriods', [], false)
    ]);
    await validateForm();
  };

  const handlePagination = (event, page: number) => {
    const offset = (page - 1) * 10;
    setOffset(offset);
  };

  if (isError) {
    dispatch(AlertActions.error(`${t(translations.pages.invoices.alerts.errorLoading())}`));
    navigate('/', { replace: true });
    return null;
  }

  if (isLoading) {
    return <CircularProgress />;
  }
  const offers = offersData!;

  return (
    //TODO replace this with the Mui DataGrid
    <Grid container>
      <ErrorBoundary boundaryTag="paidOffersBoundary">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container className="invoice-detail-modal__body__offer-count">
              {t(translations.pages.invoices.selectedOffersCount(), {
                selected: values.selectedOffers.length,
                total: offers.pagination.count
              })}
            </Grid>
          </Grid>
          {hasDisabledJobOffer && (
            <Grid item xs={12}>
              <InfoBox
                type="warning"
                expandable={true}
                title={t(
                  translations.pages.invoices.disabledJobOffers.disabledPeriodsWarningTitle()
                )}
              >
                {t(translations.pages.invoices.disabledJobOffers.disabledPeriodsWarning())}
              </InfoBox>
            </Grid>
          )}
          <Grid item xs={12}>
            <RichList
              staticHeading={
                <Grid
                  container
                  className="invoice-detail-modal__body__list-heading"
                  component={Paper}
                  elevation={3}
                  square
                >
                  <Grid item md={1} className="invoice-detail-modal__body__list-heading__item">
                    {t(translations.common.id())}
                  </Grid>
                  <Grid item md={6} className="invoice-detail-modal__body__list-heading__item">
                    {t(translations.pages.jobOffers.jobOffer())}
                  </Grid>
                  <Grid item md={3} className="invoice-detail-modal__body__list-heading__item">
                    {t(translations.pages.invoices.earliestPeriod())}
                  </Grid>
                  <Grid item md={2} className="invoice-detail-modal__body__list-heading__item">
                    {t(translations.pages.invoices.periodCount())}
                  </Grid>
                </Grid>
              }
            >
              <>
                <Grid container className="invoice-detail-modal__body__offers">
                  <FieldArray name="selectedOffers">
                    {() => {
                      return offers.data.map(o => {
                        //While the jobOffer is not older than three days, it can't be billed
                        const disabled = new Date(o.createdAt) > disabledDate;
                        if (disabled) setHasDisabledJobOffer(true);
                        return (
                          <ErrorBoundary key={o.id} boundaryTag="paidOffersItemBoundary">
                            <Tooltip
                              title={
                                disabled
                                  ? t(
                                      translations.pages.invoices.disabledJobOffers.disabledPeriodsTooltip()
                                    )
                                  : ''
                              }
                              followCursor
                            >
                              <Grid
                                key={o.id}
                                container
                                className={clsx('invoice-detail-modal__body__offers__offer', {
                                  'invoice-detail-modal__body__offers__offer--selected':
                                    values.selectedOffers.findIndex(so => so.id === o.id) !== -1,
                                  disabled: disabled
                                })}
                                onClick={() =>
                                  !disabled && handleOfferSelect({ id: o.id, title: o.title })
                                }
                              >
                                <Grid item md={1}>
                                  {o.id}
                                </Grid>
                                <Grid
                                  item
                                  md={6}
                                  className="invoice-detail-modal__body__offers__offer__title"
                                >
                                  {o.title}
                                </Grid>
                                <Grid
                                  item
                                  md={3}
                                  className="invoice-detail-modal__body__offers__offer__earliest"
                                >
                                  {new Date(o.earliestPeriod!).toLocaleDateString()}
                                </Grid>
                                <Grid
                                  item
                                  md={2}
                                  className="invoice-detail-modal__body__offers__offer__period-count"
                                >
                                  {o.periodsCount}
                                </Grid>
                              </Grid>
                            </Tooltip>
                          </ErrorBoundary>
                        );
                      });
                    }}
                  </FieldArray>
                </Grid>
              </>
            </RichList>
          </Grid>
        </Grid>
        {Math.floor(offers.pagination.count! / 10) > 0 && (
          <Grid container className="invoice-detail-modal__body__pagination">
            <Pagination
              count={Math.ceil(offers.pagination.count! / 10)}
              onChange={handlePagination}
              variant="outlined"
              shape="rounded"
              showFirstButton
              showLastButton
              size="medium"
              color="primary"
              siblingCount={2}
              boundaryCount={2}
            />
          </Grid>
        )}
      </ErrorBoundary>
    </Grid>
  );
}
